import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { PageHero } from "@/components/PageHero";
import { FoundingBadge } from "@/components/FoundingBadge";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  Camera, Loader2, Check, Save, LogOut, ExternalLink,
  Heart, PlusCircle, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Profile — user-facing account page.
 *
 * Fields users can edit: display name (a.k.a. username) and profile picture.
 * Email is shown but locked, because it's set by the OAuth provider and used
 * as the user's canonical id.
 *
 * The page purposefully never surfaces admin flags, tier fields, or any
 * server-side internal state — even if the /auth/me response includes them.
 */
export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [name, setName] = useState("");
  const [picture, setPicture] = useState(null);   // preview (data URI or URL)
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [stats, setStats] = useState(null);

  // Redirect to login if not signed in.
  useEffect(() => {
    if (user === null) navigate("/login", { replace: true });
  }, [user, navigate]);

  // Sync local form state from the loaded user.
  useEffect(() => {
    if (!user) return;
    setName(user.name || user.username || "");
    setPicture(user.picture || null);
  }, [user]);

  // Pull lightweight stats separately so we can refresh independently.
  useEffect(() => {
    if (!user) return;
    api.get("/user/stats").then(r => setStats(r.data)).catch(() => setStats(null));
  }, [user]);

  const dirty = user && ((name || "").trim() !== (user.name || user.username || "") ||
                          (picture || "") !== (user.picture || ""));

  const onPickFile = () => fileRef.current?.click();

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_500_000) {
      toast.error("Image too large — please pick something under 2.5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPicture(String(reader.result));
    reader.readAsDataURL(f);
  };

  const onSave = async () => {
    if (!dirty) return;
    setBusy(true); setSaved(false);
    try {
      await api.patch("/user/profile", {
        username: name.trim() || null,
        picture: picture || null,
      });
      await refresh();
      setSaved(true);
      toast.success("Profile updated.");
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) {
    return (
      <Layout hideRightPanel>
        <div className="text-center py-20 text-[#5B6478]">Loading…</div>
      </Layout>
    );
  }

  const initial = (name || user.email || "?").slice(0, 1).toUpperCase();

  return (
    <Layout hideRightPanel>
      <PageHero
        testId="profile-hero"
        eyebrow="Your Account"
        title="Profile"
        sub="Edit your display name, swap your avatar, and see a quick snapshot of what you've saved on Wikificial."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="profile-body">
        {/* Left: identity + avatar */}
        <section className="lg:col-span-2 rounded-2xl bg-white border border-[#E7EAF0] p-6 sm:p-7">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#5B6478] mb-3">Identity</div>

          <div className="flex items-start gap-5 mb-6">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div
                className="w-20 h-20 rounded-full bg-white border border-[#E7EAF0] overflow-hidden flex items-center justify-center shadow-sm"
                data-testid="profile-avatar"
              >
                {picture ? (
                  <img src={picture} alt="" className="w-full h-full object-cover"/>
                ) : (
                  <span className="text-[26px] font-bold text-[#0B1220]">{initial}</span>
                )}
              </div>
              <button
                type="button"
                onClick={onPickFile}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-[#0B1220] text-white flex items-center justify-center shadow hover:brightness-110 transition"
                title="Change avatar"
                aria-label="Change avatar"
                data-testid="profile-avatar-change"
              >
                <Camera size={12}/>
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFileChange}
                data-testid="profile-avatar-input"
              />
            </div>

            {/* Name + email */}
            <div className="flex-1 min-w-0 space-y-4">
              {user?.founding_number && (
                <div className="flex items-center gap-2" data-testid="profile-founding-row">
                  <FoundingBadge number={user.founding_number} size="md"/>
                  <span className="text-[11px] text-[#5B6478]">One of the first 250 members — permanent.</span>
                </div>
              )}
              <div>
                <label className="text-[10.5px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">Display name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="How should people see your name?"
                  className="w-full h-10 rounded-md bg-white border border-[#E7EAF0] px-3 text-[13px] text-[#0B1220] placeholder:text-[#8A93A6] focus:outline-none focus:border-[#0B1220]/40"
                  data-testid="profile-name-input"
                />
              </div>

              <div>
                <label className="text-[10.5px] uppercase tracking-widest font-bold text-[#5B6478] block mb-1.5">Email</label>
                <div className="w-full h-10 rounded-md bg-[#F7F8FA] border border-[#E7EAF0] px-3 text-[13px] text-[#0B1220] flex items-center justify-between" data-testid="profile-email">
                  <span className="truncate">{user.email}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#8A93A6]">Managed by Google</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-4 border-t border-[#E7EAF0]">
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[12.5px] font-semibold text-[#5B6478] hover:bg-[#F6F7FB] hover:text-[#0B1220] transition-colors"
              data-testid="profile-logout"
            >
              <LogOut size={13}/> Sign out
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || busy}
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-[#0B1220] text-white text-[12.5px] font-bold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              data-testid="profile-save"
            >
              {busy ? <><Loader2 size={13} className="animate-spin"/> Saving…</> :
               saved ? <><Check size={13}/> Saved</> :
               <><Save size={12}/> Save changes</>}
            </button>
          </div>
        </section>

        {/* Right: activity/stats */}
        <aside className="rounded-2xl bg-white border border-[#E7EAF0] p-6" data-testid="profile-stats">
          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#5B6478] mb-3">Your activity</div>

          <div className="space-y-3">
            <StatRow icon={<Heart size={13}/>} label="Saved tools" value={stats?.favorites ?? "—"} to="/favorites" testId="stat-favorites"/>
            <StatRow icon={<PlusCircle size={13}/>} label="Tools submitted" value={stats?.submissions ?? "—"} to="/submit" testId="stat-submissions"/>
            <StatRow icon={<MessageSquare size={13}/>} label="Wikifi conversations" value={stats?.conversations ?? "—"} to="/wikifi" testId="stat-conversations"/>
          </div>

          <div className="mt-6 pt-5 border-t border-[#E7EAF0]">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#5B6478] mb-2">Member since</div>
            <div className="text-[13px] text-[#0B1220] font-semibold">
              {stats?.joined ? new Date(stats.joined).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "—"}
            </div>
          </div>

          <div className="mt-6 pt-5 border-t border-[#E7EAF0]">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#F97316] hover:underline"
              data-testid="profile-dashboard-link"
            >
              Full dashboard <ExternalLink size={11}/>
            </Link>
          </div>
        </aside>
      </div>
    </Layout>
  );
}

function StatRow({ icon, label, value, to, testId }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-3 px-3 py-2 -mx-3 rounded-md hover:bg-[#F6F7FB] transition-colors"
      data-testid={testId}
    >
      <span className="flex items-center gap-2 text-[12.5px] text-[#0B1220]">
        <span className="text-[#F97316]">{icon}</span>
        {label}
      </span>
      <span className="text-[13px] font-bold text-[#0B1220]">{value}</span>
    </Link>
  );
}
